import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/Common";
import { api, errMsg } from "@/lib/api";
import { dateLabel, fmtNum, amountInWords } from "@/lib/format";

const WH = { A: "Warehouse A", B: "Warehouse B" };

const Party = ({ title, lines }) => (
  <div className="flex-1 border border-slate-300 p-3">
    <div className="text-[10px] font-bold uppercase tracking-widest text-[#F41444] mb-1">{title}</div>
    {lines.filter(Boolean).map((l, i) => <div key={i} className={i === 0 ? "font-bold" : "text-xs whitespace-pre-line"}>{l}</div>)}
  </div>
);

export default function PrintPO() {
  const { id } = useParams();
  const [po, setPo] = useState(null);
  const [co, setCo] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    Promise.all([api.get(`/purchase-orders/${id}`), api.get("/company")])
      .then(([a, b]) => { setPo(a.data); setCo(b.data); })
      .catch((e) => setError(errMsg(e)));
  }, [id]);
  if (error) return <div className="p-10 text-red-400" data-testid="print-error">{error}</div>;
  if (!po || !co) return <Loading />;

  const subtotal = po.items.reduce((s, i) => s + i.qty * (i.price || 0), 0);
  const gst = Math.round((subtotal * (co.gst_percent || 0)) / 100);
  const total = Math.round(subtotal) + gst;
  const terms = (co.po_terms || "").split("\n").map((t) => t.trim()).filter(Boolean);
  const title = po.kind === "manufacturing" ? "Manufacturing Order" : "Purchase Order";

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white py-6 print:py-0">
      <div className="no-print mx-auto mb-4 flex max-w-[210mm] justify-end gap-2">
        <Button onClick={() => window.print()} data-testid="print-po-btn"><Printer className="h-4 w-4" /> Print / Save PDF</Button>
      </div>
      <div className="mx-auto max-w-[210mm] bg-white p-10 text-[#071434] shadow-xl print:shadow-none print:p-0" data-testid="po-document"
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <div className="flex items-start justify-between border-b-4 border-[#071434] pb-4">
          <div className="flex items-center gap-4">
            <img src="/brand/leeway-logo.png" alt="Leeway Fitness" className="h-20 w-auto" />
            <div className="text-xs leading-relaxed">
              <div className="text-base font-extrabold">{co.name}</div>
              {co.address && <div className="whitespace-pre-line">{co.address}</div>}
              {co.phone && <div>Phone: {co.phone}</div>}
              {co.email && <div>Email: {co.email}</div>}
              {co.gstin && <div>GSTIN: {co.gstin}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-extrabold uppercase tracking-wide" data-testid="po-doc-title">{title}</div>
            <table className="ml-auto mt-2 text-xs">
              <tbody>
                <tr><td className="pr-3 text-slate-500">Order No.</td><td className="font-bold" data-testid="po-doc-number">{po.order_no}</td></tr>
                <tr><td className="pr-3 text-slate-500">Order Date</td><td>{dateLabel(po.order_date)}</td></tr>
                {po.expected_date && <tr><td className="pr-3 text-slate-500">Delivery By</td><td>{dateLabel(po.expected_date)}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-5 flex gap-4 text-sm">
          <Party title={po.kind === "manufacturing" ? "Workshop / Vendor" : "Supplier"}
            lines={[po.supplier || "—", po.supplier_address, po.supplier_gstin && `GSTIN: ${po.supplier_gstin}`]} />
          <Party title="Deliver To" lines={[`${co.name} — ${WH[po.warehouse]}`, co.address]} />
        </div>

        <table className="mt-5 w-full border-collapse text-xs" data-testid="po-doc-items">
          <thead>
            <tr className="bg-[#071434] text-white">
              {["S.No", "SKU", "Product Description", "Qty", "Rate (₹)", "Amount (₹)"].map((h, i) => (
                <th key={h} className={`border border-[#071434] px-2 py-2 font-semibold ${i >= 3 ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {po.items.map((it, i) => (
              <tr key={it.product_id} className="even:bg-slate-50">
                <td className="border border-slate-300 px-2 py-1.5">{i + 1}</td>
                <td className="border border-slate-300 px-2 py-1.5 font-mono">{it.sku}</td>
                <td className="border border-slate-300 px-2 py-1.5">{it.name}</td>
                <td className="border border-slate-300 px-2 py-1.5 text-right">{fmtNum(it.qty)}</td>
                <td className="border border-slate-300 px-2 py-1.5 text-right">{fmtNum(it.price || 0)}</td>
                <td className="border border-slate-300 px-2 py-1.5 text-right">{fmtNum(it.qty * (it.price || 0))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={5} className="border border-slate-300 px-2 py-1.5 text-right font-semibold">Sub Total</td><td className="border border-slate-300 px-2 py-1.5 text-right">{fmtNum(subtotal)}</td></tr>
            {co.gst_percent > 0 && <tr><td colSpan={5} className="border border-slate-300 px-2 py-1.5 text-right font-semibold">GST @ {co.gst_percent}%</td><td className="border border-slate-300 px-2 py-1.5 text-right">{fmtNum(gst)}</td></tr>}
            <tr className="bg-[#F41444] text-white"><td colSpan={5} className="border border-[#F41444] px-2 py-2 text-right font-bold">Grand Total</td><td className="border border-[#F41444] px-2 py-2 text-right font-bold" data-testid="po-doc-total">₹{fmtNum(total)}</td></tr>
          </tfoot>
        </table>
        <div className="mt-2 text-xs"><span className="font-semibold">Amount in words:</span> {amountInWords(total)}</div>
        {po.notes && <div className="mt-3 text-xs"><span className="font-semibold">Remarks:</span> {po.notes}</div>}

        {terms.length > 0 && (
          <div className="mt-6" data-testid="po-doc-terms">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#F41444] mb-1">Terms & Conditions</div>
            <ol className="list-decimal pl-5 text-xs leading-relaxed space-y-0.5">
              {terms.map((t, i) => <li key={i}>{t}</li>)}
            </ol>
          </div>
        )}

        <div className="mt-16 flex justify-between text-xs">
          <div className="w-48 border-t border-slate-400 pt-1 text-center">Supplier's Acceptance</div>
          <div className="w-56 border-t border-slate-400 pt-1 text-center">For {co.name}<br /><span className="text-slate-500">Authorised Signatory</span></div>
        </div>
      </div>
    </div>
  );
}
